import { useMemo } from 'react';
import type { WebData, SectionStatus } from '../../types/canvas';
import { ActionBar } from './ActionBar';

interface WebCardProps {
  data: WebData;
  status: SectionStatus;
  onApprove?: () => void;
  onRefine?: () => void;
  onAnother?: () => void;
  refineDisabled?: boolean;
}

interface NodePosition {
  id: string;
  name: string;
  role: string;
  x: number;
  y: number;
}

function layoutNodes(nodes: WebData['nodes'], width: number, height: number): NodePosition[] {
  const cx = width / 2;
  const cy = height / 2;
  const radius = Math.min(width, height) * 0.35;

  return nodes.map((node, i) => {
    const angle = (2 * Math.PI * i) / nodes.length - Math.PI / 2;
    return {
      ...node,
      x: cx + radius * Math.cos(angle),
      y: cy + radius * Math.sin(angle),
    };
  });
}

export function WebCard({ data, status, onApprove, onRefine, onAnother, refineDisabled }: WebCardProps) {
  const svgWidth = 600;
  const svgHeight = 280;

  const positions = useMemo(
    () => layoutNodes(data.nodes, svgWidth, svgHeight),
    [data.nodes],
  );

  const positionMap = useMemo(
    () => new Map(positions.map((p) => [p.id, p])),
    [positions],
  );

  return (
    <div className="col-span-full bg-surface border border-border rounded-lg p-6 sm:px-7">
      <h3 className="font-display italic text-lg text-foreground [font-variation-settings:'opsz'_96,'SOFT'_80] mb-1">
        Connection Web
      </h3>
      <p className="text-xs text-muted-foreground mb-4">
        {data.nodes.length} characters · {data.edges.length} connections
      </p>

      <svg
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        className="w-full h-[280px] block"
        aria-label="Character connection web"
      >
        {data.edges.map((edge, i) => {
          const from = positionMap.get(edge.from);
          const to = positionMap.get(edge.to);
          if (!from || !to) return null;
          const mx = (from.x + to.x) / 2;
          const my = (from.y + to.y) / 2;
          return (
            <g key={i}>
              <line
                x1={from.x} y1={from.y} x2={to.x} y2={to.y}
                stroke="#E5E1D8" strokeWidth="1"
              />
              <text
                x={mx} y={my - 4}
                textAnchor="middle"
                className="fill-muted-foreground"
                style={{ fontSize: '9px', fontFamily: 'General Sans, sans-serif' }}
              >
                {edge.label.length > 30 ? edge.label.slice(0, 27) + '...' : edge.label}
              </text>
            </g>
          );
        })}

        {positions.map((node) => (
          <g key={node.id}>
            <circle cx={node.x} cy={node.y} r="20" fill="#FAFAF7" stroke="#E5E1D8" strokeWidth="1.5" />
            <text
              x={node.x} y={node.y + 1}
              textAnchor="middle"
              dominantBaseline="middle"
              className="fill-foreground"
              style={{ fontSize: '13px', fontFamily: 'Fraunces, serif', fontStyle: 'italic' }}
            >
              {node.name.split(' ')[0]}
            </text>
            <text
              x={node.x} y={node.y + 30}
              textAnchor="middle"
              className="fill-placeholder"
              style={{ fontSize: '9px', fontFamily: 'General Sans, sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase' } as React.CSSProperties}
            >
              {node.role}
            </text>
          </g>
        ))}
      </svg>

      <ActionBar status={status} onApprove={onApprove} onRefine={onRefine} onAnother={onAnother} refineDisabled={refineDisabled} />
    </div>
  );
}
